import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import "./Wayfinder.css";
import Header from "../components/Header";
import WayfinderMap from "../components/WayfinderMap";
import WayfinderLocationPanel from "../components/WayfinderLocationPanel";
import WayfinderAssemblyPanel from "../components/WayfinderAssemblyPanel";
import WayfinderArrivalModal from "../components/WayfinderArrivalModal";
import { getBibData } from "../utils/wayfinderUtils";
import { useWayfinderLocation } from "../hooks/useWayfinderLocation";
import { useWayfinderRouting } from "../hooks/useWayfinderRouting";
import {
  calculateDistance,
  convertToMapboxFormat,
} from "../utils/wayfinderUtils";

const WayfinderRefactored = () => {
  const location = useLocation();
  const bibNumber = location.state?.bibNumber;

  const [viewState, setViewState] = useState({
    longitude: 151.2072222,
    latitude: -33.8402778,
    zoom: 15,
  });

  // Track if user has manually interacted with the map
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [lastUserLocation, setLastUserLocation] = useState([
    151.2072222, -33.8402778,
  ]);

  const [bibData, setBibData] = useState(null);
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  const [hasArrived, setHasArrived] = useState(false);
  const [showDirections, setShowDirections] = useState(false);

  // Use custom hooks for location and routing logic
  const {
    userLocation,
    setUserLocation,
    customLocation,
    useCurrentLocation,
    setUseCurrentLocation,
    isLoadingLocation,
    setIsLoadingLocation,
    isSearchingLocation,
    searchResults,
    showSearchResults,
    isLocationTrackingActive,
    setIsLocationTrackingActive,
    getCurrentLocation,
    handleLocationTypeChange,
    handleLocationSelect,
    handleStartLocationTracking,
  } = useWayfinderLocation(bibData);

  const {
    selectedRoute,
    setSelectedRoute,
    routeDistance,
    setRouteDistance,
    routeStartLocation,
    routeEndLocation,
    currentRouteLeg,
    getSelectedRoute,
    handleSmartRouting,
    handleDirectionsClick: handleDirectionsClickHook,
  } = useWayfinderRouting(bibData);

  // Memoize the initial distance calculation
  const initialDistanceToStart = useMemo(() => {
    if (bibData && bibData.routeStartCoordinates) {
      const routeStartCoords = convertToMapboxFormat(
        bibData.routeStartCoordinates
      );
      if (routeStartCoords) {
        const defaultLocation = [151.2072222, -33.8402778];
        return calculateDistance(
          defaultLocation[1], // lat
          defaultLocation[0], // lng
          routeStartCoords[1], // route start lat
          routeStartCoords[0] // route start lng
        );
      }
    }
    return null;
  }, [bibData]);

  // Initialize BIB data and route
  useEffect(() => {
    if (bibNumber) {
      const data = getBibData(bibNumber);
      const route = getSelectedRoute(bibNumber);
      setBibData(data);
      setSelectedRoute(route);

      // Store initial distance to start point for route switch notification logic
      if (initialDistanceToStart !== null) {
        sessionStorage.setItem(
          "initialDistanceToStart",
          initialDistanceToStart.toString()
        );
      }
    }
  }, [bibNumber, getSelectedRoute, initialDistanceToStart, setSelectedRoute]);

  // Handle location changes
  useEffect(() => {
    if (useCurrentLocation) {
      getCurrentLocation();
    } else {
      // Always reset to simulated location when not using current location
      setUserLocation([151.2072222, -33.8402778]);
      setLastUserLocation([151.2072222, -33.8402778]);
      // Reset loading state when unchecking current location
      setIsLoadingLocation(false);
      // Center map on default location (only if user hasn't manually interacted)
      if (!hasUserInteracted) {
        setViewState((prev) => ({
          ...prev,
          longitude: 151.2072222,
          latitude: -33.8402778,
        }));
      }
    }
  }, [
    useCurrentLocation,
    getCurrentLocation,
    setUserLocation,
    setIsLoadingLocation,
  ]);

  // Track user location to detect arrival at assembly point
  useEffect(() => {
    if (bibData && userLocation && !hasArrived) {
      const distanceToAssembly = calculateDistance(
        userLocation[1], // lat
        userLocation[0], // lng
        bibData.assemblyCoordinates[0], // lat
        bibData.assemblyCoordinates[1] // lng
      );

      // If user is within 100 meters (0.1 km) of assembly point, mark as arrived
      if (distanceToAssembly <= 0.1) {
        console.log("🎯 [ARRIVAL] User has arrived at assembly point!");
        setHasArrived(true);

        // Stop location tracking when arrived
        if (window.locationWatchId) {
          navigator.geolocation.clearWatch(window.locationWatchId);
          window.locationWatchId = null;
          setIsLocationTrackingActive(false);
        }
      }
    }
  }, [userLocation, bibData, hasArrived, setIsLocationTrackingActive]);

  // Handle arrival done
  const handleArrivalDone = useCallback(() => {
    setHasArrived(false);
  }, []);

  // Reset map view to center on user location
  const resetMapView = useCallback(() => {
    if (userLocation) {
      setViewState((prev) => ({
        ...prev,
        longitude: userLocation[0],
        latitude: userLocation[1],
      }));
      setHasUserInteracted(false);
    }
  }, [userLocation]);

  // Handle location confirmation
  const handleConfirm = useCallback(() => {
    if (!bibData || !selectedRoute) {
      toast.error(
        "Confirmation Number not found. Please go back and try again."
      );
      return;
    }

    // If using custom location input (optional), log it
    if (!useCurrentLocation && customLocation.trim()) {
      console.log(
        "Using custom location input:",
        customLocation,
        "(with default coordinates)"
      );
    } else if (!useCurrentLocation) {
      console.log("Using default simulated location near route start");
    }

    setLocationConfirmed(true);
  }, [bibData, selectedRoute, useCurrentLocation, customLocation]);

  // Handle directions click with custom logic
  const handleDirectionsClick = useCallback(() => {
    const newShowDirections = handleDirectionsClickHook(
      userLocation,
      bibData,
      selectedRoute,
      showDirections
    );
    setShowDirections(newShowDirections);

    if (newShowDirections) {
      // Center map to show both points
      const assemblyCoords = convertToMapboxFormat(bibData.assemblyCoordinates);
      if (assemblyCoords) {
        const centerLng = (userLocation[0] + assemblyCoords[0]) / 2;
        const centerLat = (userLocation[1] + assemblyCoords[1]) / 2;

        // Calculate bounds to ensure both points are visible
        const lngDiff = Math.abs(userLocation[0] - assemblyCoords[0]);
        const latDiff = Math.abs(userLocation[1] - assemblyCoords[1]);
        const maxDiff = Math.max(lngDiff, latDiff);

        // Set zoom level based on distance between points
        let zoom = 15; // default zoom
        if (maxDiff > 0.01) zoom = 13; // if points are far apart
        if (maxDiff > 0.05) zoom = 11; // if points are very far apart

        // Only auto-center if user hasn't manually interacted
        if (!hasUserInteracted) {
          setViewState((prev) => ({
            ...prev,
            longitude: centerLng,
            latitude: centerLat,
            zoom: zoom,
          }));
        }
      }

      // Use smart routing logic
      handleSmartRouting(userLocation, bibData, selectedRoute);
    }
  }, [
    handleDirectionsClickHook,
    userLocation,
    bibData,
    selectedRoute,
    showDirections,
  ]);

  // Handle route found
  const handleRouteFound = useCallback(
    (routeData) => {
      console.log("Route found:", routeData);
      if (routeData && routeData.distance) {
        setRouteDistance(routeData.distance);
      }
    },
    [setRouteDistance]
  );

  // Handle smart routing updates when user moves
  useEffect(() => {
    if (showDirections && userLocation && bibData && selectedRoute) {
      // Only call handleSmartRouting if user location changes, not when showDirections changes
      // This prevents duplicate calls when the directions button is clicked
      if (userLocation !== lastUserLocation) {
        handleSmartRouting(userLocation, bibData, selectedRoute);
      }
    }
  }, [
    userLocation,
    bibData,
    selectedRoute,
    lastUserLocation,
    handleSmartRouting,
  ]);

  // Create a callback for location updates that respects user interaction
  const handleLocationUpdate = useCallback(
    (newLocation) => {
      setLastUserLocation(newLocation);

      // Only auto-center map if user hasn't manually interacted
      if (!hasUserInteracted) {
        setViewState((prev) => ({
          ...prev,
          longitude: newLocation[0],
          latitude: newLocation[1],
        }));
      }
    },
    [hasUserInteracted]
  );

  // Only show error if bibNumber is missing (not just while bibData is loading)
  // if (!bibNumber) {
  //   return (
  //     <div className="wayfinder-screen">
  //       <Header />
  //       <div
  //         className="error-container"
  //         style={{
  //           minHeight: "60vh",
  //           display: "flex",
  //           flexDirection: "column",
  //           justifyContent: "center",
  //           alignItems: "center",
  //           textAlign: "center",
  //         }}
  //       >
  //         <h2>No BIB Number Found</h2>
  //         <p>Please go back and enter your BIB number.</p>
  //         <button
  //           onClick={() => navigate("/find-my-route")}
  //           className="go-back-button"
  //           style={{ marginTop: "1.5rem" }}
  //         >
  //           Go Back
  //         </button>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="wayfinder-screen">
      <Header />

      {/* Map Component */}
      <WayfinderMap
        viewState={viewState}
        setViewState={setViewState}
        userLocation={userLocation}
        bibData={bibData}
        locationConfirmed={locationConfirmed}
        showDirections={showDirections}
        routeStartLocation={routeStartLocation}
        routeEndLocation={routeEndLocation}
        selectedRoute={selectedRoute}
        onRouteFound={handleRouteFound}
        hasUserInteracted={hasUserInteracted}
        setHasUserInteracted={setHasUserInteracted}
        resetMapView={resetMapView}
      />

      {/* Conditional Panel Rendering */}
      {!locationConfirmed ? (
        <WayfinderLocationPanel
          useCurrentLocation={useCurrentLocation}
          setUseCurrentLocation={setUseCurrentLocation}
          customLocation={customLocation}
          handleLocationTypeChange={handleLocationTypeChange}
          isLoadingLocation={isLoadingLocation}
          isSearchingLocation={isSearchingLocation}
          searchResults={searchResults}
          showSearchResults={showSearchResults}
          handleLocationSelect={handleLocationSelect}
          handleConfirm={handleConfirm}
        />
      ) : (
        <WayfinderAssemblyPanel
          bibData={bibData}
          selectedRoute={selectedRoute}
          showDirections={showDirections}
          currentRouteLeg={currentRouteLeg}
          routeDistance={routeDistance}
          userLocation={userLocation}
          isLocationTrackingActive={isLocationTrackingActive}
          locationConfirmed={locationConfirmed}
          setLocationConfirmed={setLocationConfirmed}
          handleDirectionsClick={handleDirectionsClick}
          handleStartLocationTracking={handleStartLocationTracking}
          onLocationUpdate={handleLocationUpdate}
        />
      )}

      {/* Arrival Modal */}
      <WayfinderArrivalModal
        hasArrived={hasArrived}
        bibData={bibData}
        handleArrivalDone={handleArrivalDone}
      />
    </div>
  );
};

export default WayfinderRefactored;
